import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  FileText,
  Calendar,
  Clock,
  GraduationCap,
  DoorOpen,
  AlertTriangle,
  CheckCircle,
  Loader2,
  UserX,
  Trash2,
  Edit3,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { fetchFilieresGrouped, getSemestersByFiliere, semesterOptionId, optionsForNiveau } from '@/services/filieresService';
import { getGroupesByFiliere } from '@/services/groupsService';
import { fetchStaffUsers, StaffUser } from '@/services/userService';
import { fetchTeachingRooms, type RoomFlat } from '@/services/campusInfrastructureService';
import { calendarSettingsService } from '../../services/calendarSettingsService';
import type { FiliereWithOptions, SemesterWithModules } from '@/types/filiere.types';
import type { GroupeWithCounts } from '@/types/group.types';
import examScheduleService, {
  ExamConflictResult,
  CreateExamEntryDto,
  ExamCalendarEntry,
  UpdateExamEntryDto,
  ExamPeriods,
} from '../../services/examScheduleService';

interface ExamEntryModalProps {
  academicYearId: string;
  campusId: string;
  /** Display name of the campus the exam will be created on */
  campusName?: string;
  /**
   * Campuses the user may create exams on (already scoped server-side:
   * all campuses for DG/global roles, assigned ones otherwise). More than
   * one entry turns the campus line into a selector.
   */
  campuses?: { id: string; name: string }[];
  filiereId?: string;
  groupeId?: string;
  niveau?: number;
  moduleElementId?: string;
  selectedDate?: string | null;
  selectedStartTime?: string | null;
  selectedEndTime?: string | null;
  defaultSemester?: 1 | 2;
  isRattrapage?: boolean;
  allowedModuleElementIds?: string[];
  // Edit mode props
  editMode?: boolean;
  examToEdit?: ExamCalendarEntry | null;
  onClose: () => void;
  onSuccess: () => void;
  onDelete?: () => void;
}

interface ModuleElement {
  id: string;
  name: string;
  volumeHoraire?: number;
  moduleId: string;
}

const ExamEntryModal: React.FC<ExamEntryModalProps> = ({
  academicYearId,
  campusId,
  campusName,
  campuses,
  filiereId: initialFiliereId,
  groupeId: initialGroupeId,
  niveau: initialNiveau,
  moduleElementId: initialModuleElementId,
  selectedDate: initialDate,
  selectedStartTime: initialStartTime,
  selectedEndTime: initialEndTime,
  defaultSemester = 1,
  isRattrapage = false,
  allowedModuleElementIds,
  editMode = false,
  examToEdit,
  onClose,
  onSuccess,
  onDelete,
}) => {
  const { tokens } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [filieres, setFilieres] = useState<FiliereWithOptions[]>([]);
  const [semesters, setSemesters] = useState<SemesterWithModules[]>([]);
  const [groupes, setGroupes] = useState<GroupeWithCounts[]>([]);
  const [allGroupes, setAllGroupes] = useState<GroupeWithCounts[]>([]);
  const [professors, setProfessors] = useState<StaffUser[]>([]);
  const [rooms, setRooms] = useState<RoomFlat[]>([]);

  // Determine initial values based on edit mode
  const getInitialValues = () => {
    if (editMode && examToEdit) {
      return {
        filiereId: examToEdit.filiere.id,
        niveau: examToEdit.groupe.niveau,
        groupIds: [examToEdit.groupe.id, ...(examToEdit.combinedGroupIds || [])],
        moduleElementId: examToEdit.moduleElement.id,
        professorId: examToEdit.professor?.id || '',
        isExternalProctor: examToEdit.isExternalProctor,
        roomId: examToEdit.room?.id || '',
        sessionTypeId: examToEdit.sessionType?.id || '',
        examDate: examToEdit.examDate,
        startTime: examToEdit.startTime,
        endTime: examToEdit.endTime,
      };
    }
    return {
      filiereId: initialFiliereId || '',
      niveau: initialNiveau || 1,
      groupIds: initialGroupeId ? [initialGroupeId] : [],
      moduleElementId: initialModuleElementId || '',
      professorId: '',
      isExternalProctor: false,
      roomId: '',
      sessionTypeId: '',
      examDate: initialDate || '',
      startTime: initialStartTime || '08:00',
      endTime: initialEndTime || '10:00',
    };
  };

  const initialValues = getInitialValues();

  // Form state
  // Multi-campus users (DG, staff assigned to several campuses) can retarget the
  // exam's campus from the modal; single-campus staff keep it fixed
  const [selectedCampusId, setSelectedCampusId] = useState(campusId);
  const [selectedFiliereId, setSelectedFiliereId] = useState(initialValues.filiereId);
  // Option of the filière at the selected niveau (specialisation years only)
  const [selectedOptionId, setSelectedOptionId] = useState('');
  const [selectedNiveau, setSelectedNiveau] = useState<number>(initialValues.niveau);
  const [selectedSemester, setSelectedSemester] = useState<1 | 2>(defaultSemester);
  const [selectedModuleElementId, setSelectedModuleElementId] = useState(initialValues.moduleElementId);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(initialValues.groupIds);
  const [selectedProfessorId, setSelectedProfessorId] = useState(initialValues.professorId);
  const [isExternalProctor, setIsExternalProctor] = useState(initialValues.isExternalProctor);
  const [selectedRoomId, setSelectedRoomId] = useState(initialValues.roomId);
  const [selectedSessionTypeId, setSelectedSessionTypeId] = useState(initialValues.sessionTypeId);
  const [examDate, setExamDate] = useState(initialValues.examDate);
  const [startTime, setStartTime] = useState(initialValues.startTime);
  const [endTime, setEndTime] = useState(initialValues.endTime);

  // Conflict state
  const [conflictResult, setConflictResult] = useState<ExamConflictResult | null>(null);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);
  // Non-blocking warnings (outside exam period, weekend, holiday, max/day) require explicit confirmation
  const [showWarningConfirm, setShowWarningConfirm] = useState(false);

  // Exam periods for validation
  const [examPeriods, setExamPeriods] = useState<ExamPeriods | null>(null);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      if (!tokens?.accessToken) return;
      setIsLoading(true);
      try {
        const [filieresData, professorsResponse, roomsData, sessionTypesData, examPeriodsData] = await Promise.all([
          fetchFilieresGrouped(true),
          fetchStaffUsers({ role: ['TEACHER'] }),
          fetchTeachingRooms(tokens.accessToken, selectedCampusId),
          calendarSettingsService.getSessionTypes(),
          examScheduleService.getExamPeriods(academicYearId, selectedCampusId),
        ]);
        setFilieres(filieresData);
        // A preselected filière may actually be an OPTION id (e.g. L3 group flows):
        // remap to parent filière + option so the selectors reflect it
        const initId = initialValues.filiereId;
        if (initId && !filieresData.some((f: FiliereWithOptions) => f.id === initId)) {
          const parent = filieresData.find((f: FiliereWithOptions) => (f.options || []).some((o) => o.id === initId));
          if (parent) {
            setSelectedFiliereId(parent.id);
            setSelectedOptionId(initId);
          }
        }
        setProfessors(professorsResponse.data);
        setRooms(roomsData);
        setExamPeriods(examPeriodsData);

        // Session type is required by the schema but not exam-relevant: auto-pick
        // the first one when none is set — exam duration is driven by start/end times
        if (sessionTypesData.length > 0 && !initialValues.sessionTypeId) {
          setSelectedSessionTypeId(sessionTypesData[0].id);
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [selectedCampusId, tokens?.accessToken]);

  // Load semesters when filiere changes
  useEffect(() => {
    const loadFiliereData = async () => {
      if (!selectedFiliereId) {
        setSemesters([]);
        setAllGroupes([]);
        setGroupes([]);
        return;
      }
      try {
        // Groups belong to the OPTION filière on specialisation years
        const [semestersData, groupesData] = await Promise.all([
          getSemestersByFiliere(selectedFiliereId),
          getGroupesByFiliere(selectedOptionId || selectedFiliereId, academicYearId, selectedCampusId),
        ]);
        setSemesters(semestersData);
        setAllGroupes(groupesData);

        // Filter groups by niveau
        const filteredGroups = groupesData.filter((g: GroupeWithCounts) => g.niveau === selectedNiveau);
        setGroupes(filteredGroups);
      } catch (err) {
        console.error('Error loading filiere data:', err);
      }
    };
    loadFiliereData();
  }, [selectedFiliereId, selectedOptionId, academicYearId, selectedCampusId]);

  // Options applicable at the selected niveau (specialisation years)
  const availableOptions = useMemo(() => {
    const filiere = filieres.find((f) => f.id === selectedFiliereId);
    if (!filiere) return [];
    return optionsForNiveau(filiere, semesters, selectedNiveau);
  }, [filieres, selectedFiliereId, semesters, selectedNiveau]);

  // Update groups when niveau changes
  useEffect(() => {
    if (allGroupes.length > 0) {
      const filteredGroups = allGroupes.filter((g: GroupeWithCounts) => g.niveau === selectedNiveau);
      setGroupes(filteredGroups);
      // Only reset group selection if not in edit mode loading
      if (!editMode) {
        setSelectedGroupIds(prev => prev.filter(id => filteredGroups.some(g => g.id === id)));
      }
    }
  }, [selectedNiveau, allGroupes, editMode]);

  // Get available matières. Exams are tied to the selected semester; a rattrapage
  // can be scheduled at any time of year for a matière of ANY semester (e.g. an S2
  // matière retaken during S1), so rattrapage mode lists all semesters. On
  // specialisation years the semester exists once per option: keep only the
  // tronc-commun semesters plus the selected option's.
  const moduleElements = useMemo(() => {
    const collectElements = (semester: SemesterWithModules, labelSemester: boolean): ModuleElement[] => {
      const elements: ModuleElement[] = [];
      semester.modules?.forEach((mod: any) => {
        mod.elements?.forEach((el: any) => {
          elements.push({
            id: el.id,
            name: labelSemester ? `${el.name} (S${semester.number})` : el.name,
            volumeHoraire: el.volumeHoraire,
            moduleId: mod.id,
          });
        });
      });
      return elements;
    };

    const relevant = semesters.filter((s) => {
      const optId = semesterOptionId(s);
      return !optId || optId === selectedOptionId;
    });

    let elements: ModuleElement[];
    if (isRattrapage) {
      elements = relevant.flatMap((s: SemesterWithModules) => collectElements(s, true));
    } else {
      const expectedSemesterNum = (selectedNiveau - 1) * 2 + selectedSemester;
      const semester = relevant.find((s: SemesterWithModules) => s.number === expectedSemesterNum);
      elements = semester ? collectElements(semester, false) : [];
    }

    // If allowedModuleElementIds is provided, only show those matières
    if (allowedModuleElementIds && allowedModuleElementIds.length > 0) {
      return elements.filter(el => allowedModuleElementIds.includes(el.id));
    }
    return elements;
  }, [semesters, selectedNiveau, selectedSemester, allowedModuleElementIds, isRattrapage, selectedOptionId]);

  // Get filiere's nombre annees
  const selectedFiliere = filieres.find((f) => f.id === selectedFiliereId);
  const maxNiveau = selectedFiliere?.nombreAnnees || 3;

  // Shift end time when start changes, preserving the exam's current duration
  const handleStartTimeChange = (time: string) => {
    const toMins = (t: string): number => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    const duration = toMins(endTime) - toMins(startTime);
    setStartTime(time);
    if (duration > 0) {
      const totalMins = toMins(time) + duration;
      const endHours = Math.floor(totalMins / 60) % 24;
      const endMins = totalMins % 60;
      setEndTime(`${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`);
    }
  };

  // Check conflicts
  const checkConflicts = async () => {
    if (!selectedGroupIds.length || (!selectedProfessorId && !isExternalProctor) || !examDate || !startTime || !endTime) {
      return;
    }

    setIsCheckingConflicts(true);
    try {
      const professorIdForCheck = isExternalProctor ? '' : selectedProfessorId;
      const result = await examScheduleService.checkConflicts(
        academicYearId,
        selectedCampusId,
        selectedGroupIds[0],
        professorIdForCheck || '',
        examDate,
        startTime,
        endTime,
        selectedRoomId || undefined,
        editMode && examToEdit ? examToEdit.id : undefined // Exclude current exam in edit mode
      );
      setConflictResult(result);
    } catch (err) {
      console.error('Error checking conflicts:', err);
    } finally {
      setIsCheckingConflicts(false);
    }
  };

  // Auto-check conflicts when relevant fields change
  useEffect(() => {
    setShowWarningConfirm(false);
    if (selectedGroupIds.length > 0 && (selectedProfessorId || isExternalProctor) && examDate && startTime && endTime) {
      const timer = setTimeout(checkConflicts, 300);
      return () => clearTimeout(timer);
    }
    setConflictResult(null);
    return undefined;
  }, [selectedGroupIds, selectedProfessorId, isExternalProctor, examDate, startTime, endTime, selectedRoomId, selectedCampusId]);

  // Handle group selection
  const toggleGroup = (groupId: string) => {
    // In edit mode, don't allow changing groups
    if (editMode) return;

    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  // Handle submit (create or update)
  const handleSubmit = async () => {
    if (!selectedModuleElementId || !selectedGroupIds.length || (!selectedProfessorId && !isExternalProctor) || !selectedSessionTypeId || !examDate) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (availableOptions.length > 0 && !selectedOptionId) {
      setError('Veuillez sélectionner une option');
      return;
    }

    if (conflictResult?.hasConflicts) {
      setError('Veuillez résoudre les conflits avant de continuer');
      return;
    }

    // Only the configured exam period accepts exams without question: any warning
    // (outside period, weekend, holiday, max/day) must be explicitly confirmed
    if (conflictResult?.hasWarnings) {
      setShowWarningConfirm(true);
      return;
    }

    await performSave(false);
  };

  // Actual save; confirmWarnings=true when the user accepted the exception dialog
  const performSave = async (confirmWarnings: boolean) => {
    setIsSaving(true);
    setError(null);

    try {
      if (editMode && examToEdit) {
        // Update existing exam
        const updateDto: UpdateExamEntryDto = {
          professorId: isExternalProctor ? undefined : selectedProfessorId,
          isExternalProctor,
          roomId: selectedRoomId || undefined,
          sessionTypeId: selectedSessionTypeId,
          examDate,
          startTime,
          endTime,
          confirmWarnings,
        };
        await examScheduleService.updateExam(examToEdit.id, updateDto);
      } else {
        // Create new exam
        if (selectedGroupIds.length === 1) {
          const dto: CreateExamEntryDto = {
            academicYearId,
            campusId: selectedCampusId,
            filiereId: selectedOptionId || selectedFiliereId,
            groupeId: selectedGroupIds[0],
            moduleElementId: selectedModuleElementId,
            professorId: isExternalProctor ? undefined : selectedProfessorId,
            isExternalProctor,
            roomId: selectedRoomId || undefined,
            sessionTypeId: selectedSessionTypeId,
            examDate,
            startTime,
            endTime,
            isRattrapage,
            confirmWarnings,
          };
          await examScheduleService.createExam(dto);
        } else {
          await examScheduleService.createCommonExam({
            academicYearId,
            campusId: selectedCampusId,
            filiereId: selectedOptionId || selectedFiliereId,
            groupIds: selectedGroupIds,
            moduleElementId: selectedModuleElementId,
            professorId: isExternalProctor ? undefined : selectedProfessorId,
            isExternalProctor,
            roomId: selectedRoomId || undefined,
            sessionTypeId: selectedSessionTypeId,
            examDate,
            startTime,
            endTime,
            isRattrapage,
            confirmWarnings,
          });
        }
      }
      onSuccess();
    } catch (err: any) {
      const message: string = err.message || `Erreur lors de ${editMode ? 'la modification' : 'la création'} de l'examen`;
      if (!confirmWarnings && message.startsWith('Confirmation requise')) {
        // Backend backstop caught warnings the debounced local check missed:
        // refresh the check and surface the confirmation panel instead of a dead end
        setError(null);
        await checkConflicts();
        setShowWarningConfirm(true);
      } else {
        setShowWarningConfirm(false);
        setError(message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!examToEdit) return;

    setIsDeleting(true);
    setError(null);

    try {
      await examScheduleService.deleteExam(examToEdit.id);
      onDelete?.();
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression de l\'examen');
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const isRattrapageExam = editMode && examToEdit ? examToEdit.isRattrapage : isRattrapage;

  // Configured period for the current mode (exam vs rattrapage, SINGLE vs PER_SEMESTER),
  // shown as a hint under the date field — exceptions outside it require confirmation
  const activePeriod = useMemo(() => {
    if (!examPeriods) return null;
    let period: { start: string | null; end: string | null } | undefined;
    if (isRattrapageExam) {
      period = examPeriods.rattrapagePeriodType === 'SINGLE' || selectedSemester === 1
        ? examPeriods.rattrapage1
        : examPeriods.rattrapage2 || examPeriods.rattrapage1;
    } else {
      period = examPeriods.examPeriodType === 'SINGLE' || selectedSemester === 1
        ? examPeriods.period1
        : examPeriods.period2 || examPeriods.period1;
    }
    if (!period?.start || !period?.end) return null;
    return {
      start: new Date(period.start).toLocaleDateString('fr-FR'),
      end: new Date(period.end).toLocaleDateString('fr-FR'),
    };
  }, [examPeriods, isRattrapageExam, selectedSemester]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isRattrapageExam ? 'bg-amber-100' : 'bg-purple-100'}`}>
              {editMode ? (
                <Edit3 className={`w-5 h-5 ${isRattrapageExam ? 'text-amber-600' : 'text-purple-600'}`} />
              ) : (
                <FileText className={`w-5 h-5 ${isRattrapageExam ? 'text-amber-600' : 'text-purple-600'}`} />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {editMode
                  ? (isRattrapageExam ? 'Modifier le Rattrapage' : 'Modifier l\'Examen')
                  : (isRattrapageExam ? 'Planifier un Rattrapage' : 'Planifier un Examen')
                }
              </h2>
              <p className="text-sm text-gray-500">
                {editMode
                  ? 'Modifiez les informations de l\'examen'
                  : 'Remplissez les informations de l\'examen'
                }
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : (
            <>
              {/* Campus — selectable for multi-campus users (DG, multi-campus staff),
                  fixed for single-campus staff and in edit mode */}
              {campuses && campuses.length > 1 && !editMode ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campus *</label>
                  <select
                    value={selectedCampusId}
                    onChange={(e) => {
                      setSelectedCampusId(e.target.value);
                      // Groups and rooms belong to a campus: reset selections
                      setSelectedGroupIds([]);
                      setSelectedRoomId('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    {campuses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                (campusName || campuses?.length) && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                    <GraduationCap className="w-4 h-4 text-gray-400" />
                    <span>Campus :</span>
                    <span className="font-medium text-gray-900">
                      {campuses?.find((c) => c.id === selectedCampusId)?.name || campusName}
                    </span>
                  </div>
                )
              )}

              {/* Filière - Read-only in edit mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filière *</label>
                <select
                  value={selectedFiliereId}
                  onChange={(e) => {
                    if (editMode) return;
                    setSelectedFiliereId(e.target.value);
                    setSelectedOptionId('');
                    setSelectedModuleElementId('');
                    setSelectedGroupIds([]);
                    setSelectedNiveau(1);
                  }}
                  disabled={editMode}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${editMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">Sélectionner une filière...</option>
                  {filieres.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.codeFiliere} - {f.filiere}
                    </option>
                  ))}
                </select>
              </div>

              {/* Niveau - Read-only in edit mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Niveau *</label>
                <select
                  value={selectedNiveau}
                  onChange={(e) => {
                    if (editMode) return;
                    setSelectedNiveau(Number(e.target.value));
                    setSelectedOptionId('');
                    setSelectedModuleElementId('');
                    setSelectedGroupIds([]);
                  }}
                  disabled={!selectedFiliereId || editMode}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${editMode ? 'bg-gray-100 cursor-not-allowed' : 'disabled:bg-gray-100'}`}
                >
                  {Array.from({ length: maxNiveau }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n === 1 ? '1ère' : `${n}ème`} Année
                    </option>
                  ))}
                </select>
              </div>

              {/* Option — only when the selected niveau has options (specialisation years) */}
              {availableOptions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Option *</label>
                  <select
                    value={selectedOptionId}
                    onChange={(e) => {
                      if (editMode) return;
                      setSelectedOptionId(e.target.value);
                      setSelectedModuleElementId('');
                      setSelectedGroupIds([]);
                    }}
                    disabled={editMode}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${editMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Sélectionner une option...</option>
                    {availableOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.optionName || o.filiere} ({o.codeFiliere})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Groups - Read-only in edit mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Groupe(s) * {!editMode && <span className="text-xs text-gray-400">(sélectionnez un ou plusieurs groupes)</span>}
                </label>
                <div className={`flex flex-wrap gap-2 p-3 border border-gray-200 rounded-lg min-h-[48px] ${editMode ? 'bg-gray-100' : 'bg-gray-50'}`}>
                  {!selectedFiliereId ? (
                    <span className="text-sm text-gray-400">Sélectionnez d'abord une filière</span>
                  ) : availableOptions.length > 0 && !selectedOptionId ? (
                    <span className="text-sm text-gray-400">Sélectionnez d'abord une option</span>
                  ) : groupes.length === 0 ? (
                    <span className="text-sm text-gray-400">Aucun groupe pour ce niveau</span>
                  ) : (
                    groupes.map((groupe) => (
                      <button
                        key={groupe.id}
                        type="button"
                        onClick={() => toggleGroup(groupe.id)}
                        disabled={editMode}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          selectedGroupIds.includes(groupe.id)
                            ? 'bg-purple-100 text-purple-700 border border-purple-300'
                            : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'
                        } ${editMode ? 'cursor-not-allowed' : ''}`}
                      >
                        {groupe.name} ({groupe.studentCount || 0})
                      </button>
                    ))
                  )}
                </div>
                {selectedGroupIds.length > 1 && (
                  <p className="mt-1 text-xs text-purple-600">
                    Examen commun pour {selectedGroupIds.length} groupes
                  </p>
                )}
              </div>

              {/* Semester & Matière - Read-only in edit mode */}
              <div className="grid grid-cols-2 gap-4">
                {/* Semester filters the matière list for exams; a rattrapage can target any semester */}
                {!isRattrapageExam && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Semestre *</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (editMode) return;
                        setSelectedSemester(1);
                        setSelectedModuleElementId('');
                      }}
                      disabled={editMode}
                      className={`flex-1 py-2 px-3 rounded-lg border transition-colors ${
                        selectedSemester === 1
                          ? 'bg-purple-100 border-purple-300 text-purple-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      } ${editMode ? 'cursor-not-allowed' : ''}`}
                    >
                      S1
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (editMode) return;
                        setSelectedSemester(2);
                        setSelectedModuleElementId('');
                      }}
                      disabled={editMode}
                      className={`flex-1 py-2 px-3 rounded-lg border transition-colors ${
                        selectedSemester === 2
                          ? 'bg-purple-100 border-purple-300 text-purple-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      } ${editMode ? 'cursor-not-allowed' : ''}`}
                    >
                      S2
                    </button>
                  </div>
                </div>
                )}
                <div className={isRattrapageExam ? 'col-span-2' : ''}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Matière *</label>
                  <select
                    value={selectedModuleElementId}
                    onChange={(e) => !editMode && setSelectedModuleElementId(e.target.value)}
                    disabled={!selectedFiliereId || moduleElements.length === 0 || editMode}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${editMode ? 'bg-gray-100 cursor-not-allowed' : 'disabled:bg-gray-100'}`}
                  >
                    <option value="">Sélectionner...</option>
                    {moduleElements.map((el) => (
                      <option key={el.id} value={el.id}>
                        {el.name}
                      </option>
                    ))}
                  </select>
                  {selectedFiliereId && moduleElements.length === 0 && !editMode && (
                    <p className="text-xs text-amber-600 mt-1">
                      {isRattrapageExam ? 'Aucune matière en rattrapage pour ce groupe' : 'Aucune matière trouvée pour ce semestre'}
                    </p>
                  )}
                  {editMode && examToEdit && (
                    <p className="text-xs text-gray-500 mt-1">
                      {examToEdit.moduleElement.name}
                    </p>
                  )}
                </div>
              </div>

              {/* Date & Time - Editable (duration is free: set start and end times) */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="inline w-4 h-4 mr-1" />
                    Date *
                  </label>
                  <input
                    type="date"
                    value={examDate}
                    onChange={(e) => setExamDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  {activePeriod && (
                    <p className="mt-1 text-xs text-gray-500">
                      Période : {activePeriod.start} → {activePeriod.end}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Clock className="inline w-4 h-4 mr-1" />
                    Heure début *
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => handleStartTimeChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Clock className="inline w-4 h-4 mr-1" />
                    Heure fin *
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    title="Modifiable - Vous pouvez ajuster manuellement"
                  />
                </div>
              </div>

              {/* Professor with External Proctor option - Editable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <GraduationCap className="inline w-4 h-4 mr-1" />
                  Surveillant *
                </label>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 mb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isExternalProctor}
                        onChange={(e) => {
                          setIsExternalProctor(e.target.checked);
                          if (e.target.checked) {
                            setSelectedProfessorId('');
                          }
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-600 flex items-center gap-1">
                        <UserX className="w-4 h-4" />
                        Surveillant externe
                      </span>
                    </label>
                  </div>
                  {!isExternalProctor && (
                    <select
                      value={selectedProfessorId}
                      onChange={(e) => setSelectedProfessorId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="">Sélectionner un professeur...</option>
                      {professors.map((prof) => (
                        <option key={prof.id} value={prof.id}>
                          {prof.firstName} {prof.lastName}
                        </option>
                      ))}
                    </select>
                  )}
                  {isExternalProctor && (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                      Un surveillant externe sera affecté à cet examen
                    </div>
                  )}
                </div>
              </div>

              {/* Room - Editable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <DoorOpen className="inline w-4 h-4 mr-1" />
                  Salle
                </label>
                <select
                  value={selectedRoomId}
                  onChange={(e) => setSelectedRoomId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">À définir</option>
                  {rooms.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name} ({room.capacity} places)
                    </option>
                  ))}
                </select>
              </div>

              {/* Conflict Display */}
              {isCheckingConflicts && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Vérification des conflits...</span>
                </div>
              )}

              {conflictResult && !isCheckingConflicts && (
                <div className="space-y-2">
                  {conflictResult.errors.map((error, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg"
                    >
                      <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-800">
                          {error.type === 'PROFESSOR' && 'Conflit de professeur'}
                          {error.type === 'ROOM' && 'Conflit de salle'}
                          {error.type === 'GROUP' && 'Conflit de groupe'}
                        </p>
                        <p className="text-sm text-red-600">{error.message}</p>
                      </div>
                    </div>
                  ))}

                  {conflictResult.warnings.map((warning, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg"
                    >
                      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">
                          {warning.type === 'MAX_EXAMS_PER_DAY' && 'Limite d\'examens'}
                          {warning.type === 'OUTSIDE_EXAM_PERIOD' && 'Hors période'}
                          {warning.type === 'WEEKEND' && 'Week-end'}
                          {warning.type === 'HOLIDAY' && 'Jour férié'}
                        </p>
                        <p className="text-sm text-amber-600">{warning.message}</p>
                      </div>
                    </div>
                  ))}

                  {!conflictResult.hasConflicts && !conflictResult.hasWarnings && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-sm text-green-700">Aucun conflit détecté</span>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  {error}
                </div>
              )}

              {/* Warning Confirmation (exception outside exam period, weekend, holiday...) */}
              {showWarningConfirm && conflictResult && (
                <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg">
                  <p className="text-sm font-medium text-amber-800 mb-2">
                    {isRattrapageExam ? 'Ce rattrapage' : 'Cet examen'} présente des avertissements :
                  </p>
                  <ul className="list-disc list-inside text-sm text-amber-700 mb-3 space-y-1">
                    {conflictResult.warnings.map((warning, i) => (
                      <li key={i}>{warning.message}</li>
                    ))}
                  </ul>
                  <p className="text-sm text-amber-800 mb-3">
                    Voulez-vous {editMode ? 'enregistrer' : 'créer'} cette exception malgré tout ?
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowWarningConfirm(false)}
                      disabled={isSaving}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={() => performSave(true)}
                      disabled={isSaving}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 flex items-center gap-2"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Enregistrement...
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="w-4 h-4" />
                          Confirmer malgré tout
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Delete Confirmation */}
              {showDeleteConfirm && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-800 mb-3">
                    Êtes-vous sûr de vouloir supprimer cet examen ?
                  </p>
                  <p className="text-sm text-red-600 mb-4">
                    Cette action est irréversible.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 flex items-center gap-2"
                    >
                      {isDeleting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Suppression...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Confirmer la suppression
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          {/* Delete button (only in edit mode) */}
          <div>
            {editMode && !showDeleteConfirm && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSaving || isDeleting}
                className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-xl hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={isSaving || isDeleting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving || isLoading || conflictResult?.hasConflicts || showDeleteConfirm || showWarningConfirm}
              className={`px-6 py-2 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
                isRattrapageExam ? 'bg-amber-600 hover:bg-amber-700' : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {editMode ? 'Modification...' : 'Création...'}
                </>
              ) : (
                <>
                  {editMode ? <Edit3 className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                  {editMode
                    ? 'Enregistrer les modifications'
                    : (isRattrapageExam ? 'Créer le rattrapage' : 'Créer l\'examen')
                  }
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamEntryModal;
